/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Warning } from '../../store/warningsForm/types';
import { PublicWarningStatus } from '../../store/warningsForm/types';
import { mapProductStatusToWarningStatus } from '../../utils/sig-airmet-util';

export interface TimelineLayout {
  fontSize: number;
  headerHeight: number;
  weekday: 'long' | 'short' | undefined;
  stackWeekLabels: boolean;
  axisLabelSpacing: number;
}

export const getTimelineLayout = (
  viewportWidth: number,
  visibleHours: number,
): TimelineLayout => {
  const fontSize = viewportWidth < 865 ? 14 : 16;
  const weekly = visibleHours >= 336;
  const periodWidth = (viewportWidth * (weekly ? 168 : 24)) / visibleHours;
  let weekday: TimelineLayout['weekday'];
  if (!weekly && periodWidth >= 180) {
    weekday = 'long';
  } else if (!weekly && periodWidth >= 110) {
    weekday = 'short';
  }
  const stackWeekLabels = weekly && periodWidth < 180;
  return {
    fontSize,
    weekday,
    stackWeekLabels,
    headerHeight: stackWeekLabels ? 44 : 24,
    // Dates need more horizontal space than two-digit hour labels.
    axisLabelSpacing: fontSize * (weekly ? 3 : 1.5) + 12,
  };
};

export const EDGE_PADDING = 24;

export const getEffectiveStatus = (warning: Warning): PublicWarningStatus =>
  warning.status ?? mapProductStatusToWarningStatus(warning.productStatus);

export const getWarningSectionStatus = (
  warning: Warning,
): PublicWarningStatus => {
  const status = getEffectiveStatus(warning);
  if (status === PublicWarningStatus.DRAFT_PUBLISHED) {
    return PublicWarningStatus.DRAFT;
  }
  if (
    status === PublicWarningStatus.WITHDRAWN ||
    status === PublicWarningStatus.UPDATED
  ) {
    return PublicWarningStatus.EXPIRED;
  }
  return status;
};

const STATUS_SORT_ORDER: Record<PublicWarningStatus, number> = {
  [PublicWarningStatus.TODO]: 0,
  [PublicWarningStatus.DRAFT]: 1,
  [PublicWarningStatus.DRAFT_PUBLISHED]: 1,
  [PublicWarningStatus.PUBLISHED]: 2,
  [PublicWarningStatus.EXPIRED]: 3,
  [PublicWarningStatus.WITHDRAWN]: 3,
  [PublicWarningStatus.UPDATED]: 3,
  [PublicWarningStatus.IGNORED]: 3,
};

export const compareWarningsByStatus = (
  warningA: Warning,
  warningB: Warning,
): number =>
  STATUS_SORT_ORDER[getWarningSectionStatus(warningA)] -
  STATUS_SORT_ORDER[getWarningSectionStatus(warningB)];

export type TickType = 'day' | 'major' | 'dashed';

export interface Tick {
  time: Date;
  type: TickType;
}

export type BlockSize = 'xxs' | 'xs' | 's' | 'l';

const SIZE_THRESHOLDS = { xxs: 24, xs: 50, sm: 100 };

export const getTimelineX = (
  targetTime: Date,
  timelineStart: Date,
  timelineEnd: Date,
  drawableWidth: number,
  edgePadding = EDGE_PADDING,
): number => {
  const totalTimelineDuration = timelineEnd.getTime() - timelineStart.getTime();
  if (totalTimelineDuration <= 0) {
    return 0;
  }
  const elapsedMs = targetTime.getTime() - timelineStart.getTime();
  return edgePadding + (elapsedMs / totalTimelineDuration) * drawableWidth;
};

export const getTimeFromX = (
  x: number,
  timelineStart: Date,
  timelineEnd: Date,
  drawableWidth: number,
  edgePadding = EDGE_PADDING,
): Date => {
  const totalTimelineDuration = timelineEnd.getTime() - timelineStart.getTime();
  if (drawableWidth <= 0) {
    return new Date(timelineStart);
  }
  const ratio = (x - edgePadding) / drawableWidth;
  const timeMs = timelineStart.getTime() + ratio * totalTimelineDuration;
  return new Date(timeMs);
};

export const generateTicks = (
  timelineStart: Date,
  timelineEnd: Date,
  intervalHours = 1,
): Tick[] => {
  const ticks: Tick[] = [];
  const hourInterval = 3;

  const current = new Date(timelineStart);
  current.setUTCMinutes(0, 0, 0);
  if (current < timelineStart) {
    current.setUTCHours(current.getUTCHours() + 1);
  }

  while (current <= timelineEnd) {
    const hour = current.getUTCHours();

    if (hour === 0 || current.getTime() % (intervalHours * 3600000) === 0) {
      if (hour === 0) {
        ticks.push({ time: new Date(current), type: 'day' });
      } else if (hour % hourInterval === 0) {
        ticks.push({ time: new Date(current), type: 'major' });
      } else {
        ticks.push({ time: new Date(current), type: 'dashed' });
      }
    }

    current.setUTCHours(current.getUTCHours() + 1);
  }

  return ticks;
};

export const getBlockSize = (width: number): BlockSize => {
  if (width < SIZE_THRESHOLDS.xxs) {
    return 'xxs';
  }
  if (width < SIZE_THRESHOLDS.xs) {
    return 'xs';
  }
  if (width < SIZE_THRESHOLDS.sm) {
    return 's';
  }
  return 'l';
};

// Keep grid lines useful even when a month is compressed into a narrow panel.
export const getTimelineTickHours = (pixelsPerHour: number): number =>
  [1, 3, 6, 12, 24, 48, 168].find((hours) => hours * pixelsPerHour >= 8) ?? 168;
